import type {
  ComponentType,
} from 'react';
import type {
  SvgIconProps,
} from '@mui/material/SvgIcon';
import {
  PatientPCCStatusDTO,
  ProgramPCCStatusDTO,
} from 'dtos';
import {
  PCCProgram,
} from 'dtos/pCCProgram';
import {
  PCC_PROGRAM_ICONS,
} from 'views/EMR/components/PCC/programs';
import type {
  PCCProgramKey,
} from 'views/EMR/components/PCC/programs';

export type {
  PCCProgramKey,
};

export type PCCProgramState = 'enrolled' | 'optedOut' | 'eligible' | 'notEligible';

export type PCCProgramMeta = {
  key: PCCProgramKey;
  label: string;
  description: string;
  type: PCCProgram;
  Icon: ComponentType<SvgIconProps>;
};

export type PCCProgramStatus = PCCProgramMeta
  & Pick<ProgramPCCStatusDTO, 'isConsented' | 'ineligibilityReasons'>
  & {
    state: PCCProgramState;
    canEnroll: boolean;
  };

export const PCC_PROGRAMS: readonly PCCProgramMeta[] = [
  {
    key: 'tempo',
    label: 'TEMPO',
    description: 'Psychological screening',
    type: PCCProgram.Tempo,
    Icon: PCC_PROGRAM_ICONS.tempo,
  },
  {
    key: 'rtm',
    label: 'RTM',
    description: 'Remote therapeutic monitoring',
    type: PCCProgram.RTM,
    Icon: PCC_PROGRAM_ICONS.rtm,
  },
  {
    key: 'ccm',
    label: 'CCM',
    description: 'Chronic care management',
    type: PCCProgram.CCM,
    Icon: PCC_PROGRAM_ICONS.ccm,
  },
] as const;

export function getPCCProgramState(status?: ProgramPCCStatusDTO): PCCProgramState {
  let programState: PCCProgramState = 'notEligible';

  if (status?.isEnrolled) {
    programState = 'enrolled';
  } else if (status?.isOptedOut) {
    programState = 'optedOut';
  } else if (status?.isEligible) {
    programState = 'eligible';
  }

  return programState;
}

export function canEnrollPCCProgram(status?: ProgramPCCStatusDTO): boolean {
  return Boolean(
    status?.isEligible
    && !status?.isEnrolled
    && !status?.isOptedOut,
  );
}

export function getPCCPrograms(
  status?: PatientPCCStatusDTO,
  isCcmApplicable: boolean = true,
): PCCProgramStatus[] {
  const applicablePrograms = isCcmApplicable
    ? PCC_PROGRAMS
    : PCC_PROGRAMS.filter(({ type }) => type !== PCCProgram.CCM);

  return applicablePrograms.map((programMeta) => {
    const programStatus = status?.[programMeta.key];
    const state = getPCCProgramState(programStatus);

    return {
      ...programMeta,
      state,
      isConsented: programStatus?.isConsented,
      canEnroll: canEnrollPCCProgram(programStatus),
      ineligibilityReasons: programStatus?.ineligibilityReasons,
    };
  });
}

export function getEligiblePCCPrograms(
  programs: PCCProgramStatus[],
): PCCProgramStatus[] {
  return programs.filter((program) => program.canEnroll);
}

export function hasActionablePCCPrograms(programs: PCCProgramStatus[]): boolean {
  return programs.some((program) => program.canEnroll);
}
